import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import {
	ReactElement,
	ReactNode,
	useState
} from "react";
import { Icon, FontIconName } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import styles from "./AlertBar.module.less";

export type AlertMessageKind =
	| "success"
	| "info"
	| "error"
	| "warning";

type CommonAlertBarProps = {
	kind: AlertMessageKind;
	shouldBeHiddenOnMobile?: boolean;
	trackingId?: string;
	children: ReactNode;
};

type InlineAlertBarProps = {
	isInline: true;
} & (
	{
		isDismissable: false;
	} | {
		isDismissable?: true;
		onDismiss(): void;
	}
);

type NotInlineAlertBarProps = {
	isInline?: false;
	onDismiss(): void;
};

export type AlertBarProps = CommonAlertBarProps & (InlineAlertBarProps | NotInlineAlertBarProps);

export const AlertBar = (props: AlertBarProps): ReactElement => {
	const {
		children,
		kind,
		shouldBeHiddenOnMobile = false,
		isInline = false,
		trackingId
	} = props;

	const isDismissable: boolean = !props.isInline || (props.isDismissable ?? true);
	const onDismiss: (() => void) | undefined = props.isInline !== true || props.isDismissable !== false
		? props.onDismiss
		: undefined;

	const { modifier, icon } = kindToConfigurationMap[kind];

	return (
		<div
			{...setTrackingId(trackingId)}
			data-kind={kind}
			className={classNames(
				styles.root,
				modifier,
				{
					[styles.root_inline]: isInline,
					[styles.root_hideOnMobile]: shouldBeHiddenOnMobile,
				}
			)}
		>
			<div className={styles.icon}>
				<Icon value={icon} size="large" />
			</div>
			<div
				{...setTrackingId("content")}
				className={styles.content}
			>
				{ children }
			</div>
			{isDismissable && (
				<div
					className={styles.dismiss}
				>
					<IconButton
						icon="xmark"
						kind="transparent-colored"
						onClick={onDismiss}
						trackingId="dismiss"
					/>
				</div>
			)}
		</div>
	);
};
AlertBar.displayName = "AlertBar";

type AlertBarConfiguration = { modifier: string; icon: FontIconName; };

const kindToConfigurationMap: Record<AlertMessageKind, AlertBarConfiguration> = {
	["error"]: {
		modifier: styles.root_danger,
		icon: "circle-xmark"
	},
	["info"]: {
		modifier: styles.root_info,
		icon: "circle-info"
	},
	["success"]: {
		modifier: styles.root_success,
		icon: "check"
	},
	["warning"]: {
		modifier: styles.root_warning,
		icon: "triangle-exclamation"
	}
};

export type UnmanagedInlineAlertBarProps = CommonAlertBarProps & { isDismissable?: boolean; };

export const UnmanagedInlineAlertBar = (props: UnmanagedInlineAlertBarProps): ReactElement | null => {
	const [isDismissed, setIsDismissed] = useState<boolean>(false);

	if ((props.isDismissable ?? true) && isDismissed) {
		return null;
	}

	return (
		<AlertBar
			{...props}
			isInline
			onDismiss={() => setIsDismissed(true)}
		/>
	);
};
UnmanagedInlineAlertBar.displayName = "UnmanagedInlineAlertBar";
